// print.jsx — corpus-aware printing for the reader.
// PrintModal owns the draft settings and preview. PrintDocument portals the
// exact same paper markup to <body> for the browser's print/PDF renderer.
import { printScopeSegments } from "./print-selection.js";
const { useState: useStateP, useEffect: useEffectP } = React;

function printPlainText(text) {
  return String(text || "")
    .replace(/‹\d+›/g, "")
    .replace(/\\?\*+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isPrintableSegment(segment) {
  const text = `${printPlainText(segment && segment.he)} ${printPlainText(segment && segment.en)}`.trim();
  if (!text) return false;
  return !/^[\s\-–—_•·.]+$/.test(text);
}

// Small, safe subset of the corpus's inline markup: note markers, bold, and
// italics. It keeps printed pages readable without interpreting arbitrary HTML.
function renderPrintText(text, { inlineSources = null, language = "en" } = {}) {
  const tokens = String(text || "").split(/(‹\d+›|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return tokens.filter(Boolean).map((token, index) => {
    const note = token.match(/^‹(\d+)›$/);
    if (note) {
      const number = parseInt(note[1], 10);
      const source = inlineSources && inlineSources.get(number);
      const inlineText = source && printPlainText(language === "he" ? (source.he || source.en) : (source.en || source.he));
      return (
        <React.Fragment key={index}>
          <sup className="pnote-ref">{note[1]}</sup>
          {inlineText && <span className={`pnote-inline${language === "he" ? " heb" : ""}`}> {inlineText}</span>}
        </React.Fragment>
      );
    }
    if (/^\*\*[^*]+\*\*$/.test(token)) return <strong key={index}>{token.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(token)) return <em key={index}>{token.slice(1, -1)}</em>;
    return <React.Fragment key={index}>{token.replace(/\\\*/g, "*")}</React.Fragment>;
  });
}

function segRefs(seg) {
  const out = [];
  const re = /‹(\d+)›/g; let match;
  const haystack = `${seg.he || ""} ${seg.en || ""}`;
  while ((match = re.exec(haystack))) out.push(parseInt(match[1], 10));
  return out;
}

function printSourceGroup(source) {
  const raw = source && (source.displayCategory || source.category || source.type || source.relationship || "Other");
  const label = String(raw || "Other");
  if (/gemara/i.test(label)) return "Talmud";
  if (/tanach|tanakh/i.test(label)) return "Tanach";
  if (/halacha|halakhah/i.test(label)) return "Halacha";
  if (/chasidut|chassidut/i.test(label)) return "Chassidus";
  if (/quoted footnotes|mareh makom|footnotes/i.test(label)) return "Quoted Footnotes";
  return label || "Other";
}

function printSourceTitle(source) {
  const title = String(source && source.title || "").trim();
  return /^dach\s*:/i.test(title) ? "Footnote" : (title || "Source");
}

const PRINT_SOURCE_ORDER = ["Quoted Footnotes", "Tanach", "Mishnah", "Talmud", "Midrash", "Halacha", "Kabbalah", "Chassidus", "Rebbe", "Commentary", "Other"];

function sortPrintSourceGroups(groups) {
  return groups.slice().sort((a, b) => {
    const ai = PRINT_SOURCE_ORDER.indexOf(a);
    const bi = PRINT_SOURCE_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi) || a.localeCompare(b);
  });
}

function printNormRef(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

function printRefsMatch(entry, ref) {
  const target = printNormRef(ref);
  return !!target && [entry && entry.targetRef, entry && entry.ref, entry && entry.readerRef]
    .some((candidate) => printNormRef(candidate) === target);
}

function printTocInfo(ref, tocSections) {
  const entries = Array.isArray(tocSections) ? tocSections : [];
  const leafIndex = entries.findIndex((entry) => entry && !entry.isHeader && printRefsMatch(entry, ref));
  const leaf = leafIndex >= 0 ? entries[leafIndex] : null;
  const pathKey = leaf && Array.isArray(leaf.path) ? leaf.path.join("\u0000") : "";
  let header = null;
  if (pathKey) {
    for (let index = 0; index < entries.length; index++) {
      const entry = entries[index];
      if (!entry || !entry.isHeader || !Array.isArray(entry.path)) continue;
      if (entry.path.join("\u0000") === pathKey && (leafIndex < 0 || index <= leafIndex)) header = entry;
    }
  }
  return {
    label: leaf && leaf.label || "",
    heLabel: leaf && leaf.heLabel || "",
    workLabel: header && header.label || "",
    workHe: header && header.heLabel || "",
    ref: leaf && (leaf.readerRef || leaf.ref) || ref || "",
  };
}

function sectionRefForSegment(segment, sicha) {
  return segment && (segment._sectionRef || segment._sectionCanonicalRef) || sicha._liveTargetRef || sicha._liveRef || sicha.id || "";
}

function sameSet(values, expected) {
  if (values.size !== expected.length) return false;
  return expected.every((value) => values.has(value));
}

function selectedPrintSegments(sicha, opts) {
  const ids = new Set(opts && opts.segmentIds || []);
  return (sicha.segments || []).filter((segment) => ids.has(segment.id) && isPrintableSegment(segment));
}

function printSectionGroups(sicha, segments, tocSections) {
  const groups = [];
  segments.forEach((segment) => {
    const ref = sectionRefForSegment(segment, sicha);
    let group = groups[groups.length - 1];
    if (!group || printNormRef(group.ref) !== printNormRef(ref)) {
      group = { ref, info: printTocInfo(ref, tocSections), segments: [] };
      groups.push(group);
    }
    group.segments.push(segment);
  });
  return groups;
}

function printTitleModel(sicha, groups) {
  const first = groups[0] || { ref: sicha._liveRef || sicha.id || "", info: {} };
  const info = first.info || {};
  const collection = String(sicha.collection || "Mayanos Library").trim();
  const discourseHeading = /\b(?:Sicha|Maamar|Entry)\b|מאמר|שיחה/i.test(String(info.workLabel || ""));
  const workLabel = discourseHeading ? String(info.workLabel || "").trim() : "";
  const workHe = discourseHeading ? String(info.workHe || "").trim() : "";
  const sectionLabel = String(info.label || "").trim();
  const sectionHe = String(info.heLabel || "").trim();
  const exactRef = String(info.ref || first.ref || sicha._liveRef || sicha.id || "").trim();
  const fallbackHe = workHe ? "" : String(sicha.titleHe || "").trim();
  return { collection, workLabel, workHe: workHe || fallbackHe, sectionLabel, sectionHe, exactRef };
}

function printAvailableSources(segments, sources) {
  const used = new Set();
  segments.forEach((segment) => segRefs(segment).forEach((number) => used.add(number)));
  return (sources || []).filter((source) => used.has(source.n)).sort((a, b) => a.n - b.n);
}

function PrintPaper({ opts, sicha, sources, tocSections, preview = false }) {
  const segments = selectedPrintSegments(sicha, opts);
  const groups = printSectionGroups(sicha, segments, tocSections);
  const model = printTitleModel(sicha, groups);
  const showHe = opts.content !== "en";
  const showEn = opts.content !== "he";
  const allowedGroups = new Set(opts.sourceGroups || []);
  const hasSourceFilter = Array.isArray(opts.sourceGroups);
  const notesMode = opts.notesMode || (opts.notes ? "endnotes" : "none");
  const includedSources = notesMode !== "none" ? printAvailableSources(segments, sources).filter((source) => (
    !hasSourceFilter || allowedGroups.has(printSourceGroup(source))
  )) : [];
  const inlineSourceMap = notesMode === "inline" ? new Map(includedSources.map((source) => [Number(source.n), source])) : null;
  const endnotes = notesMode === "endnotes" ? includedSources : [];
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const showGroupHeadings = groups.length > 1;

  return (
    <div className={`print-doc${preview ? " print-preview-doc" : ""}`} data-direction="beis" data-print-layout={showHe && showEn ? (opts.layout || "stacked") : "stacked"}>
      <div className="pd-masthead">
        <span className="pd-brand">מַעְיָנוֹת · MAYANOS</span>
        <span className="pd-brand-sub">A Living Library of Chassidus</span>
      </div>

      <header className="pd-title">
        <div className="pd-series">{model.collection}</div>
        {showHe && model.workHe && <h1 lang="he" dir="rtl" className="heb pd-he">{model.workHe}</h1>}
        {model.workLabel && <h2 className="pd-work">{model.workLabel}</h2>}
        <div className="pd-section-title">
          {model.sectionLabel && <span>{model.sectionLabel}</span>}
          {showHe && model.sectionHe && <span lang="he" dir="rtl" className="heb">{model.sectionHe}</span>}
        </div>
        <div className="pd-ref">{model.exactRef}</div>
      </header>

      <article className="pd-body">
        {groups.map((group) => (
          <section key={group.ref || group.segments[0].id} className="pd-section">
            {showGroupHeadings && (
              <header className="pd-group-head">
                <div>
                  {group.info.workLabel && <b>{group.info.workLabel}</b>}
                  {group.info.label && <span>{group.info.label}</span>}
                </div>
                {showHe && (group.info.workHe || group.info.heLabel) && (
                  <div lang="he" dir="rtl" className="heb">{[group.info.workHe, group.info.heLabel].filter(Boolean).join(" · ")}</div>
                )}
              </header>
            )}
            {group.segments.map((segment) => (
              <div key={segment.id} className="pd-seg">
                {showHe && printPlainText(segment.he) && <p lang="he" dir="rtl" className="heb pd-seg-he">{renderPrintText(segment.he, { inlineSources: inlineSourceMap, language: "he" })}</p>}
                {showEn && printPlainText(segment.en) && <p className="pd-seg-en">{renderPrintText(segment.en, { inlineSources: inlineSourceMap, language: "en" })}</p>}
              </div>
            ))}
          </section>
        ))}
      </article>

      {endnotes.length > 0 && (
        <section className="pd-notes">
          <h3 className="pd-notes-h"><span lang="he" dir="rtl" className="heb">מַרְאֵה מְקוֹמוֹת</span> · Sources</h3>
          <ol className="pd-notes-list">
            {endnotes.map((source) => (
              <li key={source.id || source.n} className="pd-note">
                <span className="pd-note-n">{source.n}</span>
                <div className="pd-note-body">
                  <div className="pd-note-cite">
                    <b>{printSourceTitle(source)}</b>{/^dach\s*:/i.test(String(source.citation || "")) ? "" : (source.citation ? ` ${source.citation}` : "")}
                    <span className="pd-note-rel"> — {printSourceGroup(source)}{source.relationship ? `, ${source.relationship}` : ""}</span>
                  </div>
                  {showHe && printPlainText(source.he) && <p lang="he" dir="rtl" className="heb pd-note-he">{renderPrintText(source.he)}</p>}
                  {showEn && printPlainText(source.en) && <p className="pd-note-en">{renderPrintText(source.en)}</p>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <footer className="pd-colophon">
        Printed from Mayanos · {today} · {groups.length} section{groups.length !== 1 ? "s" : ""}{includedSources.length ? ` · ${includedSources.length} sources` : ""}
      </footer>
    </div>
  );
}

function PrintModal({ open, onClose, onConfirm, sicha, sources, currentSectionRef, tocSections }) {
  const allSegments = printScopeSegments(sicha, currentSectionRef, tocSections).filter(isPrintableSegment);
  const allSources = Array.isArray(sources) ? sources : (Array.isArray(window.SOURCES) ? window.SOURCES : []);
  const hasHebrew = allSegments.some((segment) => printPlainText(segment.he));
  const hasEnglish = allSegments.some((segment) => printPlainText(segment.en));
  const defaultContent = hasHebrew && hasEnglish ? "both" : hasHebrew ? "he" : "en";
  const currentIds = allSegments
    .filter((segment) => printNormRef(sectionRefForSegment(segment, sicha)) === printNormRef(currentSectionRef || sicha._liveTargetRef || sicha._liveRef || sicha.id))
    .map((segment) => segment.id);
  const allIds = allSegments.map((segment) => segment.id);
  const multipleSections = new Set(allSegments.map(segment => printNormRef(sectionRefForSegment(segment, sicha)))).size > 1;
  const defaultIds = allIds;
  const [content, setContent] = useStateP(defaultContent);
  const [printLayout, setPrintLayout] = useStateP("stacked");
  const [notesMode, setNotesMode] = useStateP("inline");
  const [sourceGroups, setSourceGroups] = useStateP(null);
  const [sel, setSel] = useStateP(() => new Set(defaultIds));

  useEffectP(() => {
    if (!open) return;
    setContent(defaultContent);
    setPrintLayout("stacked");
    setNotesMode("inline");
    setSourceGroups(null);
    setSel(new Set(defaultIds));
  }, [open, sicha.id, currentSectionRef]);

  useEffectP(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const dialog = document.querySelector('.pm');
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab' || !dialog) return;
      const controls = [...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), summary')].filter(element => element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    dialog?.querySelector('button')?.focus();
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); previousFocus?.focus?.(); };
  }, [open]);

  if (!open) return null;
  const selectedSegments = allSegments.filter((segment) => sel.has(segment.id));
  const availableSources = printAvailableSources(selectedSegments, allSources);
  const availableGroups = sortPrintSourceGroups(Array.from(new Set(availableSources.map(printSourceGroup))));
  const activeGroups = sourceGroups || new Set(availableGroups);
  const sourceGroupCounts = availableGroups.map((group) => [group, availableSources.filter((source) => printSourceGroup(source) === group).length]);
  const allGroupsOn = availableGroups.length > 0 && availableGroups.every((group) => activeGroups.has(group));
  const selectedSourceCount = notesMode !== "none" ? availableSources.filter((source) => activeGroups.has(printSourceGroup(source))).length : 0;
  const range = currentIds.length && sameSet(sel, currentIds) ? "current" : sameSet(sel, allIds) ? "full" : "custom";
  const selectedSectionCount = new Set(selectedSegments.map((segment) => printNormRef(sectionRefForSegment(segment, sicha)))).size;
  const draftOpts = { content, layout: printLayout, notesMode, segmentIds: Array.from(sel), sourceGroups: Array.from(activeGroups) };

  const chooseRange = (nextRange) => setSel(new Set(nextRange === "current" && currentIds.length ? currentIds : allIds));
  const toggle = (id) => setSel((previous) => {
    const next = new Set(previous);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSourceGroup = (group) => setSourceGroups((previous) => {
    const next = new Set(previous || availableGroups);
    next.has(group) ? next.delete(group) : next.add(group);
    return next;
  });
  const setAllSourceGroups = () => setSourceGroups(allGroupsOn ? new Set() : new Set(availableGroups));

  const doPrint = async () => {
    onConfirm(draftOpts);
    onClose();
    // The print paper is rendered in a portal after the modal closes. On
    // mobile, two frames were occasionally not enough, so window.print() ran
    // before the document existed and appeared to do nothing.
    for (let frame = 0; frame < 16; frame += 1) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.querySelector("body > .print-doc:not(.print-preview-doc)")) break;
    }
    if (document.fonts && document.fonts.ready) await document.fonts.ready.catch(() => {});
    await new Promise((resolve) => requestAnimationFrame(resolve));
    window.print();
  };

  return (
    <div className="pm-scrim" onClick={onClose}>
      <div className="pm" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Print options">
        <div className="pm-head">
          <div className="pm-head-t"><Icon d="printer" size={19} /> <h2>Print text</h2></div>
          <button className="pm-x" onClick={onClose} aria-label="Close"><Icon d="close" size={18} /></button>
        </div>

        <div className="pm-layout">
          <div className="pm-controls">
            <div className="pm-body">
              <div className="pm-field">
                <div className="pm-label">Print range</div>
                {multipleSections ? <div className="pm-seg" role="group" aria-label="Print range">
                  <button className={`pm-seg-btn${range === "current" ? " on" : ""}`} onClick={() => chooseRange("current")}>Current section</button>
                  <button className={`pm-seg-btn${range === "full" ? " on" : ""}`} onClick={() => chooseRange("full")}>Entire selected text</button>
                  {range === "custom" && <span className="pm-custom-range">Custom</span>}
                </div> : <div className="pm-field-note">The entire selected text is included.</div>}
              </div>

              <div className="pm-field">
                <div className="pm-label">Language</div>
                <div className="pm-seg">
                  <button className={`pm-seg-btn${content === "both" ? " on" : ""}`} onClick={() => setContent("both")} disabled={!hasHebrew || !hasEnglish}>Hebrew + English</button>
                  <button className={`pm-seg-btn${content === "he" ? " on" : ""}`} onClick={() => setContent("he")} disabled={!hasHebrew}>Hebrew only</button>
                  <button className={`pm-seg-btn${content === "en" ? " on" : ""}`} onClick={() => setContent("en")} disabled={!hasEnglish}>English only</button>
                </div>
              </div>

              {content === "both" && <div className="pm-field">
                <div className="pm-label">Bilingual layout</div>
                <div className="pm-seg" role="group" aria-label="Bilingual print layout">
                  <button className={`pm-seg-btn${printLayout === "stacked" ? " on" : ""}`} aria-pressed={printLayout === "stacked"} onClick={() => setPrintLayout("stacked")}>Stacked</button>
                  <button className={`pm-seg-btn${printLayout === "parallel" ? " on" : ""}`} aria-pressed={printLayout === "parallel"} onClick={() => setPrintLayout("parallel")}>Side by side</button>
                </div>
                <div className="pm-field-note">{printLayout === "parallel" ? "English on the left, Hebrew on the right; passages stay aligned." : "Hebrew above English for each passage."}</div>
              </div>}

              <div className="pm-field">
                <div className="pm-label">Footnotes</div>
                <div className="pm-seg" role="tablist" aria-label="Printed footnotes">
                  <button className={`pm-seg-btn${notesMode === "inline" ? " on" : ""}`} onClick={() => setNotesMode("inline")}>Inline</button>
                  <button className={`pm-seg-btn${notesMode === "endnotes" ? " on" : ""}`} onClick={() => setNotesMode("endnotes")}>Endnotes</button>
                  <button className={`pm-seg-btn${notesMode === "none" ? " on" : ""}`} onClick={() => setNotesMode("none")}>None</button>
                </div>
                <div className="pm-field-note">Inline places each footnote beside its marker; endnotes collect them after the text.</div>
              </div>

              {notesMode !== "none" && availableGroups.length > 0 && (
                <div className="pm-field">
                  <div className="pm-label-row">
                    <div className="pm-label">Source types</div>
                    <button className="pm-all" onClick={setAllSourceGroups}>{allGroupsOn ? "Clear all" : "Select all"}</button>
                  </div>
                  <div className="pm-seg pm-source-types">
                    {sourceGroupCounts.map(([group, count]) => (
                      <button key={group} className={`pm-seg-btn${activeGroups.has(group) ? " on" : ""}`} onClick={() => toggleSourceGroup(group)}>
                        {group} · {count}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {allSegments.length > 1 && (
                <details className="pm-passages">
                  <summary>Choose individual passages <span>{selectedSegments.length} of {allSegments.length}</span></summary>
                  <div className="pm-segs">
                    {allSegments.map((segment, index) => (
                      <button key={segment.id} className={`pm-segrow${sel.has(segment.id) ? " on" : ""}`} onClick={() => toggle(segment.id)}>
                        <span className="pm-check">{sel.has(segment.id) && <Icon d="check" size={13} />}</span>
                        <span className="pm-segn">{index + 1}</span>
                        <span className="pm-segtext">{printPlainText(segment.en || segment.he).slice(0, 82)}{printPlainText(segment.en || segment.he).length > 82 ? "…" : ""}</span>
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>

            <div className="pm-foot">
              <div className="pm-summary">{selectedSectionCount || 0} section{selectedSectionCount === 1 ? "" : "s"}{selectedSourceCount ? ` · ${selectedSourceCount} sources` : ""}</div>
              <div className="pm-actions">
                <button className="btn" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={doPrint} disabled={!selectedSegments.length}>
                  <Icon d="printer" size={16} /> Print / Save as PDF
                </button>
              </div>
            </div>
          </div>

          <aside className="pm-preview" aria-label="Print preview">
            <div className="pm-preview-head"><b>Print preview</b><span>Updates with your choices</span></div>
            <div className="pm-preview-viewport">
              <div className="pm-preview-page">
                {selectedSegments.length ? <PrintPaper opts={draftOpts} sicha={sicha} sources={allSources} tocSections={tocSections} preview /> : <div className="pm-preview-empty">Choose at least one passage to print.</div>}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function PrintDocument({ opts, sicha, sources, tocSections }) {
  if (!opts) return null;
  const allSources = Array.isArray(sources) ? sources : (Array.isArray(window.SOURCES) ? window.SOURCES : []);
  return ReactDOM.createPortal(<PrintPaper opts={opts} sicha={sicha} sources={allSources} tocSections={tocSections} />, document.body);
}

Object.assign(window, { PrintModal, PrintDocument, PrintPaper });
